// BoardMeetingsTab.tsx — Sprint 15. Table with column defs +
// Schedule button. Stateless: handlers come in as props.
import React from 'react';
import { Badge, Button, Card, Popconfirm, Space, Table, Tag, Tooltip } from 'antd';
import {
  DeleteOutlined, EditOutlined, FileTextOutlined, PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { BoardMeeting } from '../../types';
import { meetingStatusConfig, meetingTypeConfig } from './boardTypes';

export interface BoardMeetingsTabProps {
  meetings: BoardMeeting[];
  onAdd:        () => void;
  onEdit:       (meeting: BoardMeeting) => void;
  onDelete:     (id: string) => void;
  onOpenMinutes: (meeting: BoardMeeting) => void;
}

export const BoardMeetingsTab: React.FC<BoardMeetingsTabProps> = ({
  meetings, onAdd, onEdit, onDelete, onOpenMinutes,
}) => {
  const columns = [
    {
      title: 'Meeting No.', dataIndex: 'meetingNumber', key: 'num',
      render: (v: string) => <Tag color="purple">{v}</Tag>,
    },
    {
      title: 'Type', dataIndex: 'type', key: 'type',
      render: (v: string) => (
        <Tag color={meetingTypeConfig(v)?.color}>{meetingTypeConfig(v)?.label ?? v}</Tag>
      ),
    },
    { title: 'Title', dataIndex: 'title', key: 'title' },
    {
      title: 'Date', dataIndex: 'date', key: 'date',
      render: (v: string) => dayjs(v).format('MMM D, YYYY'),
    },
    {
      title: 'Time', dataIndex: 'startTime', key: 'time',
      render: (v: string, r: BoardMeeting) => `${v}${r.endTime ? ` – ${r.endTime}` : ''}`,
    },
    { title: 'Location', dataIndex: 'location', key: 'loc' },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (v: string) => (
        <Badge
          status={meetingStatusConfig(v)?.color as any}
          text={meetingStatusConfig(v)?.label ?? v}
        />
      ),
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: any, r: BoardMeeting) => (
        <Space>
          <Tooltip title="View / Edit Minutes">
            <Button size="small" icon={<FileTextOutlined />} onClick={() => onOpenMinutes(r)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(r)} />
          </Tooltip>
          <Popconfirm title="Delete meeting?" onConfirm={() => onDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>Schedule Meeting</Button>}>
      <Table
        columns={columns}
        dataSource={meetings}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: 900 }}
      />
    </Card>
  );
};
